/**
 * useMessages Hook
 * Hook para gerenciar mensagens com React Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ConversationMessageQuery,
  SendMessageRequest,
  EditMessageRequest,
  ForwardMessageRequest,
  AddReactionRequest,
  RemoveReactionRequest,
  MarkAsReadRequest,
  DeleteMessageRequest,
  MessageSearchRequest,
  BulkMessageOperationRequest,
} from '@troia-v3/types';
import { chatService } from '../services';

/**
 * Buscar mensagens de uma conversa
 */
export const useMessages = (query: ConversationMessageQuery) => {
  return useQuery({
    queryKey: ['messages', query],
    queryFn: ({ signal }) => chatService.getMessages(query, signal),
    enabled: !!query.conversationId,
    staleTime: 10 * 1000, // 10 seconds
  });
};

/**
 * Buscar mensagem por ID
 */
export const useMessage = (id?: string) => {
  return useQuery({
    queryKey: ['message', id],
    queryFn: ({ signal }) => chatService.getMessageById(id!, signal),
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Enviar mensagem
 */
export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // `handleErrorLocally: true` — ChatBox.tsx (único consumidor deste hook)
    // já mostra um toast local pra cada branch de erro; sem isso, o interceptor
    // global de `lib/api.ts` duplicava o toast no 422 de janela fechada
    // (review 2026-07-27). `chatService.sendMessage` continua default `false`
    // pros outros callers diretos (ex.: `emailService.sendEmail`).
    mutationFn: (data: SendMessageRequest) => chatService.sendMessage(data, undefined, true),
    onSuccess: (_, variables) => {
      // Invalidar mensagens da conversa
      queryClient.invalidateQueries({
        queryKey: ['messages', { conversationId: variables.conversationId }],
      });
      // Invalidar lista de conversas (para atualizar lastMessage)
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
    },
  });
};

/**
 * Enviar mensagem com arquivo
 */
export const useSendMessageWithFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      conversationId,
      file,
      caption,
      replyToMessageId,
    }: {
      conversationId: string;
      file: File;
      caption?: string;
      replyToMessageId?: string;
    }) => chatService.sendMessageWithFile(conversationId, file, caption, replyToMessageId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['messages', { conversationId: variables.conversationId }],
      });
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
    },
  });
};

/**
 * Editar mensagem
 */
export const useEditMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EditMessageRequest }) =>
      chatService.editMessage(id, data),
    onSuccess: (message) => {
      queryClient.invalidateQueries({ queryKey: ['message', message.id] });
      queryClient.invalidateQueries({
        queryKey: ['messages', { conversationId: message.conversationId }],
      });
    },
  });
};

/**
 * Encaminhar mensagem
 */
export const useForwardMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ForwardMessageRequest) => chatService.forwardMessage(data),
    onSuccess: () => {
      // Invalidar todas as conversas e mensagens após encaminhar
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['messages'], exact: false });
    },
  });
};

/**
 * Adicionar reação
 */
export const useAddReaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddReactionRequest) => chatService.addReaction(data),
    onSuccess: (message) => {
      queryClient.invalidateQueries({ queryKey: ['message', message.id] });
      queryClient.invalidateQueries({
        queryKey: ['messages', { conversationId: message.conversationId }],
      });
    },
  });
};

/**
 * Remover reação
 */
export const useRemoveReaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RemoveReactionRequest) => chatService.removeReaction(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['message', variables.messageId] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });
};

/**
 * Marcar mensagens como lidas
 */
export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MarkAsReadRequest) => chatService.markAsRead(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
    },
  });
};

/**
 * Deletar mensagem
 */
export const useDeleteMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: DeleteMessageRequest }) =>
      chatService.deleteMessage(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });
};

/**
 * Buscar mensagens
 */
export const useSearchMessages = (data: MessageSearchRequest) => {
  return useQuery({
    queryKey: ['messages-search', data],
    queryFn: ({ signal }) => chatService.searchMessages(data, signal),
    enabled: !!data.query,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Operação em lote
 */
export const useBulkMessageOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkMessageOperationRequest) => chatService.bulkMessageOperation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
    },
  });
};
